import { useApolloClient, useMutation, useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { showToast } from 'components/common/CustomToast';
import { useAdyenCardAddEnabled } from 'hooks/useAdyenCardAddEnabled';
import { ADYEN_INIT_CARD_ADD } from 'graphql/mutations/adyenInitCardAdd';
import { CREATE_CARD } from 'graphql/mutations/createCard';
import { REGISTER_WALLET_PAYMENT_METHOD } from 'graphql/mutations/registerWalletPaymentMethod';
import i18next from 'i18next';
import { useRef, useState } from 'react';
import { Linking } from 'react-native';
import { country, featureCountryCode } from 'states/persistInStorage';
import { usePaymentStore } from 'stores/paymentStore';
import {
  AdyenWalletType,
  GatewayType,
  Mutation,
  MutationCreateCardArgs,
  MutationAdyenInitCardAddArgs,
  MutationRegisterWalletPaymentMethodArgs,
} from 'types/generatedSchemaTypes';
import { OnboardingNavProps } from 'types/navigation';
import { PaymentDataProps } from 'types/PaymentData';
import { trackAdyenCardAdd } from 'utils/adyenAnalytics';
import { checkoutChannelForPlatform } from 'utils/adyenChannel';
import { isWeb } from 'utils/deviceUtils';
import Logger from 'utils/Logger';
import { refreshCustomerBestEffort } from 'utils/refreshCustomerBestEffort';
import { getRedirectUrl, redirectExternal } from 'utils/urlUtils';
import { useUiStore } from 'stores/uiStore';

type AddWalletTarget = 'modal' | 'onboarding';

const returnUrl = getRedirectUrl('credit-card');
const adyenReturnUrl = getRedirectUrl('adyen-redirect');

const paymentData: PaymentDataProps = {
  title: 'payment.addPaymentCard',
  icon: 'plus',
  action: 'addCardModal',
  testID: 'add-credit-card-button',
};

const useAddPaymentMethod = () => {
  const countryCode = useReactiveVar(country);
  const appCountry = useReactiveVar(featureCountryCode);
  const { navigate } = useNavigation<OnboardingNavProps>();
  const client = useApolloClient();
  // The wallet whose Adyen init is in flight; lets a row list spin only the
  // tapped wallet row. The ref backs the double-tap guard for card and wallet
  // inits alike because state is stale within the same tick.
  const [pendingWalletType, setPendingWalletType] =
    useState<AdyenWalletType | null>(null);
  const adyenInitInFlight = useRef(false);

  // Adyen card-add provider gate (flag + app country + email; dark by default,
  // no Nets fallback once true). Shared hook so the entry point, the add-payment
  // modal and onboarding all gate identically.
  const useAdyenCardAdd = useAdyenCardAddEnabled();

  // Single source for the Adyen init input: the hook-level default (addCard)
  // and the addWallet call-time override must stay field-identical — a wallet
  // add is the same initiation plus walletType, by construction.
  const buildInitCardAddInput = (walletType?: AdyenWalletType) => ({
    // The Adyen path intentionally keys countryCode on `appCountry`
    // (featureCountryCode) — the SAME source the flag gate
    // (isAdyenCardAddEnabledForCountry) keys on — so routing and
    // merchant-init agree. The gate returns false for a null appCountry, so
    // reaching this Adyen branch guarantees it is non-null (no-fallback
    // safety); `?? countryCode` is a defensive belt-and-braces only. The
    // Nets createCard path below deliberately stays on `country`.
    countryCode: appCountry ?? countryCode,
    language: i18next.language || 'FI',
    returnUrl: adyenReturnUrl,
    // True surface of this initiation, persisted server-side as wallet
    // consent evidence - same value the later Checkout calls report.
    checkoutChannel: checkoutChannelForPlatform(),
    ...(walletType ? { walletType } : {}),
  });

  const [createCard, { loading: loadingCreateCard }] = useMutation<
    Mutation,
    MutationCreateCardArgs
  >(CREATE_CARD, {
    variables: {
      input: {
        countryCode,
        language: i18next.language || 'FI',
        returnUrl,
      },
    },
    onCompleted: (data) => {
      if (data?.createCard?.cardUrl) {
        usePaymentStore
          .getState()
          .setCreditCardCreationTerminalUrl(data.createCard.cardUrl);
      }
    },
    onError: () => showToast('error.fetchingPaymentHighwayFormFailed', 'error'),
  });

  const [adyenInitCardAdd, { loading: loadingAdyenInit }] = useMutation<
    Mutation,
    MutationAdyenInitCardAddArgs
  >(ADYEN_INIT_CARD_ADD, {
    // Customer refresh is best-effort AFTER a successful init result (see
    // refreshCustomerBestEffort) — not via awaitRefetchQueries, which would
    // reject the mutation and orphan session/onCompleted when GET_CUSTOMER fails.
    variables: {
      input: buildInitCardAddInput(),
    },
    onCompleted: (data, clientOptions) => {
      const session = data?.adyenInitCardAdd;
      if (session?.cardId && session?.transactionId) {
        trackAdyenCardAdd('init');
        // The per-call variables (clientOptions) tell us whether THIS init was a
        // plain card add or a wallet add — walletType is only ever present on the
        // addWallet call-time override below, never on the hook-level default
        // variables addCard relies on. Reading it here (rather than threading a
        // second stash path) keeps a single source of truth for the session shape.
        const walletType = (
          clientOptions?.variables?.input as { walletType?: AdyenWalletType }
        )?.walletType;
        usePaymentStore.getState().setAdyenCardAddSession({
          cardId: session.cardId,
          transactionId: session.transactionId,
          ...(walletType ? { walletType } : {}),
        });
      }
    },
    // Init fires at flow start, before any Adyen UI, so an error simply shows a
    // retryable toast without ever opening the sheet. No Nets fallback.
    onError: () => showToast('error.adyenCardAddInit', 'error'),
  });

  const [registerWalletPaymentMethod, { loading: loadingAddMobilePay }] =
    useMutation<Mutation, MutationRegisterWalletPaymentMethodArgs>(
      REGISTER_WALLET_PAYMENT_METHOD,
      {
        onCompleted: async (data) => {
          if (
            data?.registerWalletPaymentMethod
              ?.walletTokenRegistrationRedirectUrl
          ) {
            try {
              const universalLink =
                data.registerWalletPaymentMethod
                  .walletTokenRegistrationRedirectUrl;
              if (isWeb) {
                redirectExternal(universalLink);
              } else {
                const supported = await Linking.canOpenURL(universalLink);
                if (supported) {
                  await Linking.openURL(universalLink);
                }
              }
              useUiStore.getState().setSheetModal('');
            } catch (error) {
              Logger.error(`Failed to open MobilePay URL: ${error}`);
              showToast('error.failedToOpenMobilePay', 'error');
            }
          }
        },
        onError: () => {
          showToast('error.fetchingMobilePayFormFailed', 'error');
        },
      }
    );

  // After a successful init: occupy the local wallet slot synchronously when
  // needed, then fire-and-forget GET_CUSTOMER. Customer.paymentCards currently
  // filters ADDING out, so awaiting cannot warm a fresh wallet stub — the local
  // marker is the process occupancy signal. Handoff must not wait on refresh.
  const afterSuccessfulAdyenInit = (walletType?: AdyenWalletType): void => {
    if (walletType) {
      usePaymentStore.getState().occupyAdyenWalletSlot(walletType);
    }
    void refreshCustomerBestEffort(client);
  };

  // Runs the Adyen card-add init and reports success. On error the mutation's
  // onError surfaces the toast and we return false so the caller leaves the
  // slot closed — the no-fallback path. errorPolicy:'all' keeps the rejection
  // from bubbling out of addCard.
  const initAdyenCardAdd = async (): Promise<boolean> => {
    if (adyenInitInFlight.current) {
      return false;
    }
    adyenInitInFlight.current = true;
    try {
      const { data } = await adyenInitCardAdd({ errorPolicy: 'all' });
      const session = data?.adyenInitCardAdd;
      // Require BOTH ids — matches the onCompleted stash, so "success" can never
      // open the slot with the session var left unset. On a real init error the
      // mutation's onError shows the toast; returning false keeps the slot closed
      // (the no-fallback path).
      if (!(session?.cardId && session?.transactionId)) {
        return false;
      }
      afterSuccessfulAdyenInit();
      return true;
    } finally {
      adyenInitInFlight.current = false;
    }
  };

  const addCard = async ({ action }: PaymentDataProps = paymentData) => {
    if (useAdyenCardAdd) {
      switch (action) {
        case 'addCardOnboarding':
          if (await initAdyenCardAdd()) {
            navigate('addCardOnboarding');
          }
          break;
        case 'addCardModal':
          if (await initAdyenCardAdd()) {
            useUiStore.getState().setSheetModal('addPaymentMethod');
          }
          break;
        default:
          break;
      }
      return;
    }
    switch (action) {
      case 'addCardOnboarding':
        await createCard();
        navigate('addCardOnboarding');
        break;
      case 'addCardModal':
        await createCard();
        useUiStore.getState().setSheetModal('addPaymentMethod');
        break;
      default:
        break;
    }
  };
  // Stored-wallet add (Apple Pay / Google Pay): the SAME init mutation as
  // addCard, with walletType set, so the BFF starts a wallet tokenization
  // instead of a plain card one. Adyen-only (no Nets fallback exists for
  // wallets) — the caller gates wallet rows on useAdyenCardAddEnabled(), so
  // reaching here with the flag off would be a caller bug; stay defensive
  // rather than silently no-op the flag check away here too.
  //
  // The onboarding target hosts the wallet sheet on the addCardOnboarding
  // screen so its terminal handoffs continue onboarding; the default target is
  // the account sheet modal.
  const addWallet = async (
    walletType: AdyenWalletType,
    target: AddWalletTarget = 'modal'
  ): Promise<boolean> => {
    if (!useAdyenCardAdd || adyenInitInFlight.current) {
      return false;
    }
    adyenInitInFlight.current = true;
    setPendingWalletType(walletType);
    try {
      const { data } = await adyenInitCardAdd({
        errorPolicy: 'all',
        variables: {
          input: buildInitCardAddInput(walletType),
        },
      });
      const session = data?.adyenInitCardAdd;
      if (!(session?.cardId && session?.transactionId)) {
        return false;
      }
      afterSuccessfulAdyenInit(walletType);
      if (target === 'onboarding') {
        navigate('addCardOnboarding');
      } else {
        useUiStore.getState().setSheetModal('addPaymentMethod');
      }
      return true;
    } finally {
      adyenInitInFlight.current = false;
      setPendingWalletType(null);
    }
  };

  const addMobilePay = async () => {
    const mutationInput = {
      countryCode,
      language: 'FI',
      returnUrl: getRedirectUrl('mobile-pay'),
      paymentMethod: 'MOBILEPAY',
      paymentProvider: 'AIMO_ONE_FI',
      gatewayType: 'NETS' as GatewayType,
    };
    await registerWalletPaymentMethod({
      variables: {
        input: mutationInput,
      },
    });
  };

  return {
    addCard,
    loadingAddCard:
      loadingCreateCard || (loadingAdyenInit && pendingWalletType === null),
    addMobilePay,
    loadingAddMobilePay,
    addWallet,
    pendingWalletType,
  };
};

export default useAddPaymentMethod;
